import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { QuotationsRowItems } from '../../models/quotations/Quotations';
import useQuotations from '../../hooks/quotations/useQuotations';
import SnackbarUtils from '../../utils/SnackbarUtils';
import useConfirmDialog from '../../hooks/dialogs/useConfirmDialog';
import { styled, Tooltip, Typography } from '@mui/material';
import { formatDate } from '../../utils/dateFormatter';
import DescriptionIcon from '@mui/icons-material/Description';

const StyledTitle = styled(Typography)(() => ({
    cursor: 'pointer',
}));

export const QuotationsTableRow: React.FC<QuotationsRowItems> = (props) => {
    const { row, index, forceRefresh } = props;
    const { t } = useTranslation('quotations', {
        keyPrefix: 'QUOTATIONS',
    });
    const labelId = `enhanced-table-checkbox-${index}`;
    const navigate = useNavigate();
    const quotations = useQuotations();
    const confirmDialog = useConfirmDialog();

    const editQuotation = () => {
        navigate('/quotation/edit/' + row.id);
    };

    const showQuotation = () => {
        navigate('/quotation/' + row.id);
    };

    const copyQuotation = () => {
        quotations
            .copyQuotation(row.id)
            .then(() => {
                SnackbarUtils.success(t('MESSAGES.COPIED'));
                forceRefresh();
            })
            .catch(() => {
                SnackbarUtils.error(t('MESSAGES.COPIED_ERROR'));
            });
    };

    const base64ToArrayBuffer = (data) => {
        let binaryString = window.atob(data);
        let binaryLen = binaryString.length;
        let bytes = new Uint8Array(binaryLen);
        for (let i = 0; i < binaryLen; i++) {
            let ascii = binaryString.charCodeAt(i);
            bytes[i] = ascii;
        }
        return bytes;
    };

    const toPDF = () => {
        quotations
            .createPDFQuotation(row.id, row.use_logo ? 1 : 0)
            .then((resp) => {
                // let arrBuffer = base64ToArrayBuffer(resp);
                const url = window.URL.createObjectURL(
                    new Blob([resp], { type: 'application/pdf' })
                );
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', row.name + '-arajanlat');

                // Append to html link element page
                document.body.appendChild(link);

                // Start download
                link.click();

                // Clean up and remove the link
                link.parentNode.removeChild(link);
            })
            .catch(() => {});
    };

    const deleteQuotation = () => {
        quotations
            .deleteQuotation(row.id)
            .then(() => {
                SnackbarUtils.success(t('MESSAGES.DELETED'));
                forceRefresh();
            })
            .catch(() => {
                SnackbarUtils.error(t('MESSAGES.DELETED_ERROR'));
            });
    };

    const handleClickDelete = () => {
        confirmDialog.showDialog({
            title: t('QUOTATION_DELETE_CONFIRM_DIALOG.TITLE'),
            content: t('QUOTATION_DELETE_CONFIRM_DIALOG.CONTENT'),
            confirmText: t('QUOTATION_DELETE_CONFIRM_DIALOG.CONFIRM'),
            cancelText: t('QUOTATION_DELETE_CONFIRM_DIALOG.CANCEL'),
            onConfirm: () => deleteQuotation(),
        });
    };

    const handleClickCopy = () => {
        confirmDialog.showDialog({
            title: t('QUOTATION_COPY_CONFIRM_DIALOG.TITLE'),
            content: t('QUOTATION_COPY_CONFIRM_DIALOG.CONTENT'),
            confirmText: t('QUOTATION_COPY_CONFIRM_DIALOG.CONFIRM'),
            cancelText: t('QUOTATION_COPY_CONFIRM_DIALOG.CANCEL'),
            onConfirm: () => copyQuotation(),
        });
    };

    return (
        <>
            <TableRow hover onClick={showQuotation} sx={{ cursor: 'pointer' }}>
                <TableCell component="th" id={labelId} scope="row">
                    {row.id}
                </TableCell>
                <TableCell>
                    <StyledTitle>{row.name}</StyledTitle>
                </TableCell>
                <TableCell>{row.buyer?.name}</TableCell>
                <TableCell>{formatDate(row.valid_from)}</TableCell>
                <TableCell
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'nowrap',
                    }}
                >
                    <Tooltip title={t('TABLE_ROW.DETAILS')}>
                        <DescriptionIcon
                            onClick={(e) => {
                                showQuotation();
                                e.stopPropagation();
                            }}
                            sx={{ cursor: 'pointer' }}
                        />
                    </Tooltip>

                    <Tooltip title={t('TABLE_ROW.EDIT')}>
                        <EditIcon
                            onClick={(e) => {
                                editQuotation();
                                e.stopPropagation();
                            }}
                            sx={{ cursor: 'pointer' }}
                        />
                    </Tooltip>
                    <Tooltip title={t('TABLE_ROW.COPY')}>
                        <ContentCopyIcon
                            onClick={(e) => {
                                handleClickCopy();
                                e.stopPropagation();
                            }}
                            sx={{ cursor: 'pointer' }}
                        />
                    </Tooltip>

                    <Tooltip title={t('TABLE_ROW.DOWNLOAD')}>
                        <PictureAsPdfIcon
                            onClick={(e) => {
                                toPDF();
                                e.stopPropagation();
                            }}
                            sx={{ cursor: 'pointer' }}
                        />
                    </Tooltip>
                    <Tooltip title={t('TABLE_ROW.DELETE')}>
                        <DeleteIcon
                            onClick={(e) => {
                                handleClickDelete();
                                e.stopPropagation();
                            }}
                            sx={{ cursor: 'pointer' }}
                        />
                    </Tooltip>
                </TableCell>
                <TableCell align="right">
                    {/*<Typography
                        onClick={showQuotation}
                        sx={{ cursor: 'pointer' }}
                    >
                        {t('TABLE_ROW.DETAILS')}
                    </Typography>*/}
                </TableCell>
            </TableRow>
        </>
    );
};
